import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ConfigEditor, hashConfig, type EditableConfig, type Sample } from "./components/ConfigEditor";
import { ReadinessHeader } from "./components/ReadinessHeader";
import { CardsBoard } from "./components/CardsBoard";
import { DisclaimerBanner } from "./components/DisclaimerBanner";
import { CoverageScopePanel } from "./components/CoverageScopePanel";
import { RequirementInspector } from "./components/RequirementInspector";
import { ReadinessOverview } from "./components/ReadinessOverview";
import { ComplianceHorizon } from "./components/ComplianceHorizon";
import { UseCaseIntelligence } from "./components/UseCaseIntelligence";
import { ActionBriefing } from "./components/ActionBriefing";
import { OpportunityBubbleChart } from "./components/OpportunityBubbleChart";
import { OverviewDeck } from "./components/OverviewDeck";
import {
  RegulatoryLens,
  applyLens,
  emptyLens,
  lensActiveCount,
  type LensState,
  type LensFacet,
} from "./components/RegulatoryLens";
import { SignalDrawer } from "./components/SignalDrawer";
import { EvidencePackPreview } from "./components/EvidencePackPreview";
import { CountingGuide } from "./components/CountingGuide";
import { RetrievalPanel } from "./components/RetrievalPanel";
import {
  acceptSignalAsUseCase,
  dismissSignal,
  evaluate,
  fetchCoverageClaim,
  fetchSeedConfigs,
  fetchUseCaseSignals,
  fetchWp29Signals,
  promoteSignal,
  revertSignal,
  runDiscovery,
  searchRetrievedContext,
  triggerWp29MockIngest,
  triggerWp29LiveFeeder,
  loadLatestWp29Feed,
  LATEST_WP29_FEED_WEEK,
  triggerVocIngest,
  reassignSignalTopics,
  ingestTrisNotification,
  fetchSeedRules,
  type CoverageClaimResponse,
  type EvaluateResponse,
  type SeedRule,
  type ReadinessSummary,
} from "./services/api";
import { readinessDelta, type ReadinessDelta } from "./lib/readiness-delta";
import { deriveDiscoveryQueries } from "./services/discovery-query";
import { NeedsAttention } from "./components/NeedsAttention";
import { ENFORCEMENT_CASES } from "./data/enforcement-cases";
import type { LifecycleLayer, PromotedRule, UseCasePublicSignal, Wp29Signal } from "@regpulse/contracts";

const CONTRACT_VERSION_DEFAULT = "0.1.0-w1";

export function App() {
  const [samples, setSamples] = useState<Sample[] | null>(null);
  const [draft, setDraft] = useState<EditableConfig | null>(null);
  const [appliedHash, setAppliedHash] = useState<string | null>(null);
  const [evaluation, setEvaluation] = useState<EvaluateResponse | null>(null);
  const [readinessDeltaState, setReadinessDeltaState] = useState<ReadinessDelta | null>(null);
  const prevReadinessRef = useRef<ReadinessSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedCardId, setSelectedCardId] = useState<string | null>(null);
  const [lens, setLens] = useState<LensState>(emptyLens());
  const [signals, setSignals] = useState<Wp29Signal[]>([]);
  const [signalDrawerOpen, setSignalDrawerOpen] = useState(false);
  const [ingesting, setIngesting] = useState(false);
  const [latestFeedBusy, setLatestFeedBusy] = useState(false);
  const [vocIngesting, setVocIngesting] = useState(false);
  const [trisIngesting, setTrisIngesting] = useState(false);
  const [liveFeederBusy, setLiveFeederBusy] = useState(false);
  const [liveFeederStatus, setLiveFeederStatus] = useState<
    { kind: "ok" | "error"; message: string } | null
  >(null);
  const [vocChannels, setVocChannels] = useState<Record<
    "openai" | "exa" | "mcp",
    string
  > | null>(null);
  const [discovering, setDiscovering] = useState(false);
  const [discoveryMode, setDiscoveryMode] = useState<
    { exa: string; raglight: string; llm: string } | null
  >(null);
  const [coverageClaim, setCoverageClaim] = useState<CoverageClaimResponse | null>(
    null,
  );
  const [seedRules, setSeedRules] = useState<SeedRule[]>([]);
  // D4: advisory U "Case intelligence" signals accepted from discovery (server-backed). Shown
  // alongside the owner's seed enforcement caseset; never board obligations, never coverage.
  const [acceptedUseCaseSignals, setAcceptedUseCaseSignals] = useState<UseCasePublicSignal[]>([]);

  // Lens = highlight overlay, not a filter: the lifecycle map keeps ALL cards
  // in place and dims those the active lens does not match (ADR-0007 — no lane
  // move, no re-evaluation). null when no lens is active (nothing dimmed).
  const matchedCardIds = useMemo(() => {
    if (!evaluation || lensActiveCount(lens) === 0) return null;
    return new Set(applyLens(evaluation.cards, lens).map((c) => c.id));
  }, [evaluation, lens]);

  const selectedCard = useMemo(() => {
    if (!evaluation || !selectedCardId) return null;
    return evaluation.cards.find((c) => c.id === selectedCardId) ?? null;
  }, [evaluation, selectedCardId]);

  const selectedOutcomeReason = useMemo(() => {
    if (!evaluation || !selectedCardId) return undefined;
    return evaluation.outcomes.find((o) => o.ruleId === selectedCardId)?.outcome.reason;
  }, [evaluation, selectedCardId]);

  const toggleLens = useCallback((facet: LensFacet, value: string) => {
    setLens((prev) => {
      const nextFacet = new Set(prev[facet]);
      if (nextFacet.has(value)) nextFacet.delete(value);
      else nextFacet.add(value);
      return { ...prev, [facet]: nextFacet };
    });
  }, []);

  const clearLens = useCallback(() => setLens(emptyLens()), []);

  // Audit G9 — readiness delta: when a new evaluation arrives, diff its readiness
  // against the previous one so the reviewer sees what moved (ingest / promotion).
  // No baseline on the first evaluation (delta = null).
  useEffect(() => {
    if (!evaluation) {
      prevReadinessRef.current = null;
      setReadinessDeltaState(null);
      return;
    }
    const prev = prevReadinessRef.current;
    setReadinessDeltaState(prev ? readinessDelta(prev, evaluation.readiness) : null);
    prevReadinessRef.current = evaluation.readiness;
  }, [evaluation]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const seed = await fetchSeedConfigs();
        if (cancelled) return;
        const list: Sample[] = Object.entries(seed.samples).map(([id, config]) => ({
          id,
          label: seed.labels?.[id] ?? id,
          config: config as EditableConfig,
        }));
        setSamples(list);
        // Load the first sample into the draft so the editor isn't empty.
        if (list.length > 0 && draft === null) {
          setDraft(structuredClone(list[0]!.config));
        }
        // Coverage claim is advisory; a failure here must not block the demo.
        try {
          const claim = await fetchCoverageClaim();
          if (!cancelled) setCoverageClaim(claim);
        } catch {
          /* claim panel renders a graceful empty state */
        }
        try {
          const sr = await fetchSeedRules();
          if (!cancelled) setSeedRules(sr.rules);
        } catch {
          /* Compliance Horizon stays hidden if seed rules are unavailable */
        }
      } catch (err) {
        if (cancelled) return;
        setError(
          `Could not reach the applicability-api worker. ` +
            `Is it running on http://127.0.0.1:8787? (${(err as Error).message})`,
        );
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLoadSample = useCallback(
    (sampleId: string) => {
      const s = samples?.find((x) => x.id === sampleId);
      if (!s) return;
      setDraft(structuredClone(s.config));
      // Loading a sample is a deliberate reset — clear applied snapshot so
      // the user sees "not yet run" until they explicitly press Run.
      setAppliedHash(null);
      setEvaluation(null);
      setError(null);
    },
    [samples],
  );

  const handleReset = useCallback(() => {
    if (samples && samples.length > 0) {
      setDraft(structuredClone(samples[0]!.config));
      setAppliedHash(null);
      setEvaluation(null);
      setError(null);
    }
  }, [samples]);

  const runEvaluate = useCallback(async () => {
    if (!draft) return;
    setLoading(true);
    setError(null);
    try {
      const result = await evaluate(draft);
      setEvaluation(result);
      setAppliedHash(hashConfig(draft));
    } catch (err) {
      setError(`Evaluator failed: ${(err as Error).message}`);
    } finally {
      setLoading(false);
    }
  }, [draft]);

  const handleVocIngest = useCallback(
    async (params: {
      brand: string;
      keyword?: string;
      topicSweep?: boolean;
      topicIds?: string[];
      competitors?: string[];
    }) => {
      setVocIngesting(true);
      setError(null);
      try {
        const res = await triggerVocIngest(params);
        setVocChannels(res.channels);
        const sig = await fetchWp29Signals();
        setSignals(sig.signals);
        if (draft) {
          const result = await evaluate(draft);
          setEvaluation(result);
          setAppliedHash(hashConfig(draft));
        }
      } catch (err) {
        setError(`VOC ingest failed: ${(err as Error).message}`);
        throw err;
      } finally {
        setVocIngesting(false);
      }
    },
    [draft],
  );

  // RFC-0007 — paste a TRIS (2015/1535) notification email → country-tagged
  // advisory national-draft candidate (same review queue). Rethrows on failure
  // so the drawer keeps the pasted text for a retry.
  const handleTrisIngest = useCallback(
    async (emailText: string) => {
      setTrisIngesting(true);
      setError(null);
      try {
        await ingestTrisNotification(emailText);
        const sig = await fetchWp29Signals();
        setSignals(sig.signals);
        if (draft) {
          const result = await evaluate(draft);
          setEvaluation(result);
          setAppliedHash(hashConfig(draft));
        }
      } catch (err) {
        setError(`TRIS ingest failed: ${(err as Error).message}`);
        throw err;
      } finally {
        setTrisIngesting(false);
      }
    },
    [draft],
  );

  const handleIngestSignals = useCallback(async () => {
    setIngesting(true);
    setError(null);
    try {
      await triggerWp29MockIngest();
      const sig = await fetchWp29Signals();
      setSignals(sig.signals);
      // Re-run evaluate so linkedSignals / signalBundle refresh.
      if (draft) {
        const result = await evaluate(draft);
        setEvaluation(result);
        setAppliedHash(hashConfig(draft));
      }
    } catch (err) {
      setError(`WP29 ingest failed: ${(err as Error).message}`);
    } finally {
      setIngesting(false);
    }
  }, [draft]);

  // Audit G8 — load the latest REAL WP29 weekly feed snapshot (the actual
  // deployed-feeder output archived to R2) into the local core so the 草案 axis
  // shows real data for review. Merges into the review bundle (does not wipe VOC).
  const handleLoadLatestFeed = useCallback(async () => {
    setLatestFeedBusy(true);
    setError(null);
    try {
      await loadLatestWp29Feed();
      const sig = await fetchWp29Signals();
      setSignals(sig.signals);
      if (draft) {
        const result = await evaluate(draft);
        setEvaluation(result);
        setAppliedHash(hashConfig(draft));
      }
    } catch (err) {
      setError(`载入真实 WP29 feed 失败: ${(err as Error).message}`);
    } finally {
      setLatestFeedBusy(false);
    }
  }, [draft]);

  // Audit G5 — trigger the DEPLOYED wp29-feeder (live weekly pull → signed
  // candidates into core). Fail-closed: a missing/unreachable feeder surfaces an
  // error status rather than silently doing nothing. Mock path stays for dev.
  const handleLiveFeeder = useCallback(async () => {
    setLiveFeederBusy(true);
    setLiveFeederStatus(null);
    try {
      const res = await triggerWp29LiveFeeder();
      setLiveFeederStatus({
        kind: "ok",
        message: `实时拉取成功：${res.weekId ?? "?"} · ${res.count ?? 0} 条信号已入审查队列`,
      });
      const sig = await fetchWp29Signals();
      setSignals(sig.signals);
      if (draft) {
        const result = await evaluate(draft);
        setEvaluation(result);
        setAppliedHash(hashConfig(draft));
      }
    } catch (err) {
      setLiveFeederStatus({
        kind: "error",
        message: `实时拉取失败（feeder 未配置或不可达）：${(err as Error).message}`,
      });
    } finally {
      setLiveFeederBusy(false);
    }
  }, [draft]);

  // Reassign a VOC signal's topic attribution (advisory curation; replaces its
  // market_voice spine edges server-side). The next 重新分析 reflects it.
  const handleReassignTopics = useCallback(async (signalId: string, topicIds: string[]) => {
    setError(null);
    try {
      await reassignSignalTopics(signalId, topicIds);
    } catch (err) {
      setError(`重新归类失败: ${(err as Error).message}`);
      throw err;
    }
  }, []);

  const handleSelectRuleFromSignal = useCallback((ruleId: string) => {
    setSelectedCardId(ruleId);
    setSignalDrawerOpen(false);
  }, []);

  /**
   * Single client-side write path for candidate→canonical decisions.
   * Mirrors Lesson §16: the server is the source of truth — we POST,
   * then refresh signals + re-evaluate so the UI always reads back the
   * server-confirmed lifecycleState. No optimistic local mutation.
   */
  const handleSignalDecision = useCallback(
    async (
      signalId: string,
      decision: "promote" | "dismiss" | "revert",
      opts?: { persistTier?: "canonical" | "raglight" },
    ) => {
      // Reviewer identity comes from `window.localStorage.regpulse_reviewer`
      // if set (lets devs identify themselves in audit logs without a
      // login layer for the prototype). Defaults to "reviewer@local".
      const reviewer =
        (typeof window !== "undefined" && window.localStorage?.getItem("regpulse_reviewer")) ||
        "reviewer@local";
      if (decision === "promote") {
        await promoteSignal(signalId, reviewer, undefined, { persistTier: opts?.persistTier });
      } else if (decision === "dismiss") {
        await dismissSignal(signalId, reviewer);
      } else {
        await revertSignal(signalId, reviewer);
      }
      const sig = await fetchWp29Signals();
      setSignals(sig.signals);
      // Reverting a canonical promotion removes its rule server-side; re-evaluate so the
      // board card disappears (and any linkedSignals refresh). No-op without a draft.
      await runEvaluate();
    },
    [runEvaluate],
  );

  /**
   * D3 (ADR-0010 Decision 8): accept a candidate AS canonical by submitting a reviewer-signed
   * PromotedRule (authored in the drawer's form). The server validates + origin-checks + appends
   * it; we refetch signals and re-evaluate so the materialised card appears on the board. This is
   * the only advisory→canonical write path — no auto-promotion.
   */
  const handlePromoteCanonical = useCallback(
    async (signalId: string, rule: PromotedRule) => {
      await promoteSignal(signalId, rule.reviewer, undefined, {
        persistTier: "canonical",
        promotedRule: rule,
      });
      const sig = await fetchWp29Signals();
      setSignals(sig.signals);
      await runEvaluate();
    },
    [runEvaluate],
  );

  const loadUseCaseSignals = useCallback(async () => {
    const res = await fetchUseCaseSignals();
    setAcceptedUseCaseSignals(res.signals);
  }, []);

  /**
   * D4: accept a discovered candidate into U "Case intelligence" (a third ADVISORY destination —
   * never canonical, never the board). Re-loads the U signal list so the section refreshes.
   */
  const handleAcceptUseCase = useCallback(
    async (signalId: string) => {
      const reviewer =
        (typeof window !== "undefined" && window.localStorage?.getItem("regpulse_reviewer")) ||
        "reviewer@local";
      await acceptSignalAsUseCase(signalId, reviewer);
      await loadUseCaseSignals();
    },
    [loadUseCaseSignals],
  );

  // Load any server-stored U case-intelligence signals on mount (advisory; non-fatal if the API
  // is unreachable — the section still shows the owner's seed enforcement caseset).
  useEffect(() => {
    void loadUseCaseSignals().catch(() => undefined);
  }, [loadUseCaseSignals]);

  /** Markets for config-derived discovery: EU baseline + selected member states. */
  const configMarkets = useMemo(() => {
    if (!draft) return [] as string[];
    const tm = draft.targetMarkets;
    return [...(tm.eu_baseline ? ["EU"] : []), ...tm.selected_member_states];
  }, [draft]);

  /** Hint keyword for VOC collect from the loaded vehicle config (demo: ISA / DMS). */
  const vocKeywordDefault = useMemo(() => {
    if (!draft) return "lane keeping";
    if (draft.adasFeatureInventory?.isa === true) return "intelligent speed assistance";
    if (draft.dms?.fitted === true) return "driver monitoring";
    if (draft.adasFeatureInventory?.elks === true) return "lane keeping";
    return "lane keeping";
  }, [draft]);

  // Discovered candidates awaiting review — the "new to review" attention count.
  const discoveredCandidateCount = useMemo(
    () => signals.filter((s) => s.lifecycleState === "candidate" && s.bundleId.startsWith("disc-")).length,
    [signals],
  );

  // First-screen scope facts + per-lane counts for the progressive-disclosure summary.
  const scope = useMemo(() => {
    if (!evaluation) return null;
    const applicable = evaluation.cards.filter((c) => c.applicabilityCategory === "applicable");
    const domains = new Set(applicable.map((c) => c.regulatoryDomain));
    const laneOrder: LifecycleLayer[] = ["A", "B", "C", "D", "P", "U"];
    const laneCounts = new Map<LifecycleLayer, number>(laneOrder.map((l) => [l, 0]));
    for (const c of applicable) laneCounts.set(c.layer, (laneCounts.get(c.layer) ?? 0) + 1);
    // U is not a card-routed lane (it's the Use Case Intelligence catalog, ADR-0008),
    // so show "U catalog" rather than a misleading "U 0".
    const laneCountsLabel = laneOrder
      .map((l) => (l === "U" ? "U catalog" : `${l} ${laneCounts.get(l) ?? 0}`))
      .join(" · ");
    return { applicable: applicable.length, domainCount: domains.size, laneCountsLabel };
  }, [evaluation]);

  const handleDiscover = useCallback(async (query: string) => {
    setDiscovering(true);
    try {
      const res = await runDiscovery(query);
      setDiscoveryMode(res.mode);
      const sig = await fetchWp29Signals();
      setSignals(sig.signals);
    } finally {
      setDiscovering(false);
    }
  }, []);

  const handleDiscoverForConfig = useCallback(async () => {
    if (!evaluation) return;
    const queries = deriveDiscoveryQueries(evaluation.cards, configMarkets);
    if (queries.length === 0) return;
    setDiscovering(true);
    try {
      let lastMode: { exa: string; raglight: string; llm: string } | null = null;
      for (const q of queries) {
        const res = await runDiscovery(q);
        lastMode = res.mode;
      }
      if (lastMode) setDiscoveryMode(lastMode);
      const sig = await fetchWp29Signals();
      setSignals(sig.signals);
    } finally {
      setDiscovering(false);
    }
  }, [evaluation, configMarkets]);

  const dirty = useMemo(() => {
    if (!draft || appliedHash === null) return false;
    return hashConfig(draft) !== appliedHash;
  }, [draft, appliedHash]);

  return (
    <div className="app-shell">
      <DisclaimerBanner />
      <CoverageScopePanel claim={coverageClaim?.claim ?? null} />
      <header className="app-header">
        <div className="app-header__brand">
          <span className="name">RegPulse Core</span>
          <span className="tag">
            prototype · contract {evaluation?.contractVersion ?? CONTRACT_VERSION_DEFAULT}
          </span>
        </div>
        <dl className="app-header__meta">
          <div>
            <dt>seed rules</dt>
            <dd>{evaluation?.cards.length ?? "—"}</dd>
          </div>
          <div>
            <dt>applicable</dt>
            <dd>{evaluation?.readiness.counters.applicable ?? "—"}</dd>
          </div>
          <div>
            <dt>hidden</dt>
            <dd>{evaluation?.readiness.counters.hidden_by_config ?? "—"}</dd>
          </div>
          <div>
            <dt>needs config</dt>
            <dd>{evaluation?.readiness.counters.unknown_needs_config ?? "—"}</dd>
          </div>
          <div>
            <dt>state</dt>
            <dd data-testid="page-state">
              {loading
                ? "evaluating"
                : evaluation === null
                  ? "pre-check"
                  : dirty
                    ? "board stale"
                    : "board ready"}
            </dd>
          </div>
          <div>
            <button
              data-testid="signals-toggle"
              aria-label={
                signals.length > 0
                  ? `Open signals drawer (${signals.length} candidate signals)`
                  : "Open signals drawer (none ingested yet)"
              }
              onClick={() => setSignalDrawerOpen(true)}
              style={{
                position: "relative",
                padding: "6px 10px",
                fontSize: 12,
                border: "1px solid var(--color-border-strong)",
                background: signals.length > 0 ? "var(--ft-market-force-soft)" : "var(--color-surface)",
                color: signals.length > 0 ? "var(--ft-market-force)" : "var(--color-text-primary)",
                borderRadius: "var(--radius-full)",
                cursor: "pointer",
                fontWeight: 600,
              }}
            >
              <span aria-hidden style={{ marginRight: 4 }}>
                🛎
              </span>
              Signals
              <span
                data-testid="signals-count"
                style={{
                  marginLeft: 6,
                  padding: "0 6px",
                  borderRadius: "var(--radius-full)",
                  background:
                    signals.length > 0 ? "var(--ft-market-force)" : "var(--color-border-strong)",
                  color: "white",
                  fontFamily: "var(--font-mono)",
                  fontSize: 11,
                }}
              >
                {signals.length}
              </span>
            </button>
          </div>
        </dl>
      </header>

      <ReadinessHeader readiness={evaluation?.readiness ?? null} loading={loading} />

      <main className="app-main">
        <aside style={{ display: "grid", gap: "var(--space-16)" }}>
          {draft && samples ? (
            <ConfigEditor
              draft={draft}
              appliedHash={appliedHash}
              onChange={setDraft}
              onRun={runEvaluate}
              onReset={handleReset}
              onLoadSample={handleLoadSample}
              samples={samples}
              loading={loading}
            />
          ) : (
            <div className="panel">
              <h2 className="panel__title">Loading samples…</h2>
              <p className="panel__caption">Fetching seed configurations from the API.</p>
            </div>
          )}
        </aside>
        <section style={{ display: "grid", gap: "var(--space-16)" }}>
          {error && (
            <div className="error-banner" role="alert">
              {error}
            </div>
          )}
          {!evaluation && !error && (
            <div className="panel">
              <h2 className="panel__title">Awaiting evaluation</h2>
              <p className="panel__caption">
                Edit any field on the left, then press <strong>Run applicability</strong> to
                populate the ABCDPU board.
              </p>
            </div>
          )}
          {dirty && evaluation && (
            <div
              role="status"
              style={{
                padding: "var(--space-8) var(--space-12)",
                background: "var(--status-unknown-config-soft)",
                border: "1px dashed var(--status-unknown-config)",
                borderRadius: "var(--radius-4)",
                fontSize: 12,
              }}
              data-testid="stale-banner"
            >
              <strong>Board is stale.</strong> The config has been edited since the last run.
              Press <em>Re-run applicability</em> to refresh.
            </div>
          )}
          {evaluation && scope && (
            <div
              data-testid="scope-strip"
              style={{
                fontSize: 12,
                color: "var(--color-text-secondary)",
                padding: "var(--space-4) 0",
                borderBottom: "1px solid var(--color-border)",
              }}
            >
              <strong style={{ color: "var(--color-text-primary)" }}>
                {draft?.projectName ?? "Configuration"}
              </strong>{" "}
              · {configMarkets.length > 0 ? configMarkets.join("+") : "EU"} ·{" "}
              {scope.applicable} obligations / {scope.domainCount} domains
            </div>
          )}
          {evaluation && (
            <NeedsAttention
              cards={evaluation.cards}
              newToReviewCount={discoveredCandidateCount}
              onOpenReview={() => setSignalDrawerOpen(true)}
            />
          )}
          {evaluation && (
            <OverviewDeck evaluation={evaluation} cards={evaluation.cards} lens={lens} />
          )}
          {evaluation && (
            <ReadinessOverview
              evaluation={evaluation}
              cards={evaluation.cards}
              delta={readinessDeltaState}
            />
          )}
          {evaluation && seedRules.length > 0 && (
            <ComplianceHorizon rules={seedRules} cards={evaluation.cards} />
          )}
          {evaluation && <CountingGuide cards={evaluation.cards} />}
          {evaluation && <RetrievalPanel onSearch={searchRetrievedContext} />}
          {evaluation && (
            <RegulatoryLens
              cards={evaluation.cards}
              lens={lens}
              onToggle={toggleLens}
              onClear={clearLens}
            />
          )}
          {evaluation && scope && (
            <details data-testid="lifecycle-explore">
              <summary
                style={{
                  cursor: "pointer",
                  fontSize: 13,
                  fontWeight: 600,
                  padding: "var(--space-8) 0",
                }}
              >
                Lifecycle map · {scope.laneCountsLabel}
              </summary>
              <CardsBoard
                cards={evaluation.cards}
                outcomes={evaluation.outcomes}
                onSelectCard={setSelectedCardId}
                selectedCardId={selectedCardId}
                matchedCardIds={matchedCardIds}
              />
            </details>
          )}
          {/* Static catalog; gated on `evaluation` so the U band appears with
              the rest of the lifecycle board, not because it needs the result. */}
          {evaluation && (
            <UseCaseIntelligence
              boardCards={evaluation.cards}
              caseSignals={[...ENFORCEMENT_CASES, ...acceptedUseCaseSignals]}
            />
          )}
          {/* Content-first analysis surfaces: the briefing answers "what moved
              and what do I research next" (real draft headlines + pain quotes);
              the map answers "what's the portfolio shape" (honest 1D draft-
              activity swarm until pain exists, then real quadrants). The old
              CrossAnalysisMatrix was killed — its categorical pills rendered
              47/48 rows identically; its rule-stock audit role lives in the
              briefing's 法规存量 footer. */}
          <ActionBriefing signalCount={signals.length + acceptedUseCaseSignals.length} />
          <OpportunityBubbleChart signalCount={signals.length + acceptedUseCaseSignals.length} />
          {evaluation && (
            <EvidencePackPreview
              cards={evaluation.cards}
              appliedHash={appliedHash}
              signalBundle={evaluation.signalBundle}
              projectName={draft?.projectName ?? ""}
              readinessSnapshot={evaluation.readiness}
              linkedSignals={evaluation.linkedSignals}
              coverageClaim={coverageClaim?.claim ?? null}
            />
          )}
        </section>
      </main>
      {selectedCard && (
        <RequirementInspector
          card={selectedCard}
          outcomeReason={selectedOutcomeReason}
          onClose={() => setSelectedCardId(null)}
        />
      )}
      <SignalDrawer
        open={signalDrawerOpen}
        onClose={() => setSignalDrawerOpen(false)}
        signals={signals}
        bundle={evaluation?.signalBundle ?? null}
        linkedSignals={evaluation?.linkedSignals ?? []}
        onIngest={handleIngestSignals}
        onDecide={handleSignalDecision}
        onPromoteCanonical={handlePromoteCanonical}
        onAcceptUseCase={handleAcceptUseCase}
        ingesting={ingesting}
        onTriggerLive={handleLiveFeeder}
        liveFeederBusy={liveFeederBusy}
        liveFeederStatus={liveFeederStatus}
        onLoadLatestFeed={handleLoadLatestFeed}
        latestFeedBusy={latestFeedBusy}
        latestFeedWeek={LATEST_WP29_FEED_WEEK}
        onSelectRule={handleSelectRuleFromSignal}
        onDiscover={handleDiscover}
        onDiscoverForConfig={handleDiscoverForConfig}
        discovering={discovering}
        discoveryMode={discoveryMode}
        configQueryAvailable={evaluation != null}
        onVocIngest={handleVocIngest}
        onTrisIngest={handleTrisIngest}
        trisIngesting={trisIngesting}
        vocIngesting={vocIngesting}
        vocChannels={vocChannels}
        onReassignTopics={handleReassignTopics}
        vocBrandDefault="Chery"
        vocKeywordDefault={vocKeywordDefault}
      />
    </div>
  );
}
